/**
 * Global plain-language labels for agent tool calls — the ONE dictionary shared
 * by every agent-SDK surface (Quality / Tech-Support / Analyst Co-Pilots, the
 * admin Agent Console, and the embedded CPLEX panel). Admins see the raw tool
 * name, SQL and ids in the trace; everyone else sees the friendly phrase here.
 *
 * MAINTENANCE: this is the source of truth — when an agent gains a new tool, add
 * a row. Tools with no row fall back to a neutral "Looked something up" so a new
 * capability is never surfaced raw to a non-admin. Mark internal-plumbing tools
 * (voice_reply, ToolSearch, …) `hidden` so they drop out of a layman trace.
 *
 * Gating lives in components/agentConsole/ToolCallTrace.tsx (reads useIsAdmin()).
 */
import type { ToolCallView } from './agentConsole';

export interface FriendlyTool {
  icon: string;
  label: string;
}

interface ToolLabel {
  icon: string;
  label: string;
  /** Internal plumbing — never shown to non-admins (friendlyToolLabel → null). */
  hidden?: boolean;
  /** Optional sharpening from the call's KB name / input (e.g. which table). */
  refine?: (view: ToolCallView) => string | undefined;
}

/** "Drafted a question to Jake Butchko" — who the ask is for, from the call's
 *  own target (a person) or team, so the trace never names the wrong desk.
 *  "Drafted", not "Sent": the call only files a draft that waits for the
 *  user's Send (owner ruling bz2bb6), and "Sent a question to Jake" read as
 *  if it had gone (UX round 2026-10-06). */
function askRecipientHint(view: ToolCallView): string | undefined {
  const target = view.input.target;
  if (typeof target === 'string' && target.trim()) return `Drafted a question to ${target.trim()}`;
  const team = view.input.team;
  if (typeof team === 'string' && team.trim()) return `Drafted a question to the ${team.trim()} team`;
  return undefined;
}

/** A read whose SQL touches a known table → a sharper, still-plain phrase. */
function tableHint(view: ToolCallView): string | undefined {
  const sql = typeof view.input?.sql === 'string' ? view.input.sql.toLowerCase() : '';
  if (!sql) return undefined;
  if (sql.includes('quality_issues') || sql.includes('packaging_feedback') || sql.includes('red_flag'))
    return 'Checked existing quality issues';
  if (/\border/.test(sql)) return 'Looked up the order';
  if (sql.includes('customer')) return 'Looked up the customer';
  if (sql.includes('email')) return 'Checked the email history';
  return undefined;
}

/** Sharpen a document search by the requested doc types (SDS / TDS / specs). */
function docHint(view: ToolCallView): string | undefined {
  const raw = (view.input as { doc_types?: unknown }).doc_types;
  const dt = Array.isArray(raw) ? raw.map((d) => String(d)).join(' ').toLowerCase() : '';
  if (dt.includes('sds')) return 'Looked up safety data sheets (SDS)';
  if (dt.includes('tds')) return 'Looked up technical data sheets (TDS)';
  return undefined;
}

/** A weather lookup with a past date → historical phrasing (else "current"). */
const CHANGE_TYPE_WORDS: Record<string, string> = {
  formula: 'formula',
  vendor: 'vendor',
  raw_material: 'raw-material',
  production_process: 'production-process',
  batch_size: 'batch-size',
  equipment: 'equipment',
  packaging: 'packaging',
  spec: 'spec',
  other: 'other',
};

function meetingTranscriptsHint(view: ToolCallView): string | undefined {
  const input = view.input as { query?: unknown; meeting_id?: unknown };
  const hasQuery = typeof input.query === 'string' && input.query.trim().length > 0;
  if (hasQuery) return 'Searched the meeting transcripts';
  if (typeof input.meeting_id === 'string' && input.meeting_id) return 'Read a meeting transcript';
  return undefined;
}

function meetingSignalsHint(view: ToolCallView): string | undefined {
  const ct = (view.input as { change_type?: unknown }).change_type;
  if (typeof ct === 'string' && CHANGE_TYPE_WORDS[ct]) {
    return `Checked the meeting notes for ${CHANGE_TYPE_WORDS[ct]} changes`;
  }
  return undefined;
}

function weatherHint(view: ToolCallView): string | undefined {
  const d = (view.input as { date?: unknown }).date;
  if (typeof d === 'string' && d.trim()) return `Checked the weather on ${d.trim()}`;
  return undefined;
}

// Keyed by the BARE tool name (services/agentConsole.ts::prettyToolName output,
// i.e. ToolCallView.name — already stripped of the mcp__<server>__ prefix).
// Source of truth for the tool universe: services/agent_console/tools/registry.py.
const TOOL_LABELS: Record<string, ToolLabel> = {
  // ── Databases ────────────────────────────────────────────────────────────
  pg_query: { icon: '🔎', label: 'Looked up our records', refine: tableHint },
  mysql_query: { icon: '📒', label: 'Looked it up in Sage (our ERP)' },
  pg_schema: { icon: '🗂️', label: 'Checked how the data is organized' },
  mysql_schema: { icon: '🗂️', label: 'Checked how the data is organized' },
  get_schema: { icon: '🗂️', label: 'Checked how the data is organized' },

  // ── Knowledge banks & the technical document library ─────────────────────
  query_knowledge_bank: {
    icon: '📖',
    label: 'Checked the knowledge bank',
    refine: (v) => (v.kb ? `Checked the “${v.kb}”` : undefined),
  },
  kb_search: {
    icon: '📖',
    label: 'Checked the knowledge bank',
    refine: (v) => (v.kb ? `Checked the “${v.kb}”` : undefined),
  },
  report_learning: {
    icon: '📝',
    label: 'Recorded a note in the knowledge bank',
    refine: (v) => (v.kb ? `Recorded a note in the “${v.kb}”` : undefined),
  },
  search_documents: {
    icon: '📄',
    label: 'Searched the technical documents (SDS / TDS / specs)',
    refine: docHint,
  },

  // ── Customer Pilot (one customer's relationship, kanban 63b736fe) ─────────
  customer_account: { icon: '🏢', label: "Looked up the customer's account" },
  customer_orders: { icon: '📦', label: "Listed the customer's orders" },
  customer_order_story: { icon: '🧾', label: 'Pulled one order end to end' },
  customer_invoices: { icon: '💵', label: "Listed the customer's invoices" },
  customer_invoice_detail: { icon: '💵', label: 'Opened one invoice' },
  customer_products_bought: { icon: '🛒', label: 'Totted up what the customer buys' },
  customer_pricing: { icon: '🏷️', label: "Checked the customer's special prices" },
  customer_complaints: { icon: '⚠️', label: "Checked the customer's quality complaints" },
  customer_emails: { icon: '✉️', label: 'Looked through email with the customer' },
  customer_email_read: { icon: '✉️', label: 'Read one email' },
  product_lookup: { icon: '🔎', label: 'Looked up the product catalogue' },

  // ── Environmental data (Quality Co-Pilot RCA) ────────────────────────────
  get_weather: { icon: '🌦️', label: 'Checked the weather', refine: weatherHint },
  lot_order_lookup: { icon: '🏷️', label: 'Traced the lot to its shipments' },
  search_meeting_signals: {
    icon: '🗒️',
    label: 'Checked the meeting notes for changes',
    refine: meetingSignalsHint,
  },
  search_meeting_transcripts: {
    icon: '🎙️',
    label: 'Read the meeting transcripts',
    refine: meetingTranscriptsHint,
  },
  request_information: {
    icon: '🙋',
    label: 'Drafted a question to a colleague',
    refine: askRecipientHint,
  },
  check_information_requests: { icon: '📥', label: 'Checked for answers from colleagues' },
  cancel_information_request: { icon: '🚫', label: 'Withdrew a question to a colleague' },

  // ── Email handling (Tech Support / Kirk) ─────────────────────────────────
  mark_kirk_email_processed: { icon: '✅', label: 'Marked the email as handled' },

  // ── Production planning (CPLEX modeler) ──────────────────────────────────
  open_cplex_version: { icon: '📂', label: 'Opened a planning model' },
  list_cplex_versions: { icon: '🗂️', label: 'Listed planning-model versions' },
  preview_cplex_solve: { icon: '🧮', label: 'Test-ran the plan (preview)' },
  submit_cplex_version: { icon: '📤', label: 'Saved a new planning-model version' },

  // ── Capture / output channels ────────────────────────────────────────────
  submit_signal_report: { icon: '📝', label: 'Published the report' },
  submit_entity_grades: { icon: '🎓', label: 'Submitted letter grades for review' },
  // Successful attach_file frames are lifted into file cards (the card replaces
  // the chip — components/agentConsole/agentFileCards.ts); this label covers
  // pending/failed ones.
  attach_file: { icon: '📎', label: 'Prepared a file' },
  // Conversation-channel capture frames: accepted ones are lifted into cards
  // (conversationMessageCards.ts); a rejected one keeps its chip.
  send_conversation_message: { icon: '✉️', label: 'Sent a message to the salesperson' },
  record_expired_quote_decision: { icon: '✅', label: 'Recorded the decision on the expired quote' },
  propose_order_readback: { icon: '📋', label: 'Read the order back for confirmation', hidden: true },
  create_draft_order: { icon: '📦', label: 'Created the draft order (not submitted)' },
  // Internal plumbing — hidden from a layman trace entirely.
  voice_reply: { icon: '🔊', label: 'Spoke a summary', hidden: true },
  ToolSearch: { icon: '🧰', label: 'Loaded a tool', hidden: true },
};

const FALLBACK: FriendlyTool = { icon: '•', label: 'Looked something up' };

/**
 * Plain-language label for a tool call, or `null` to HIDE the call from a
 * non-admin trace (internal plumbing). Unknown tools get a neutral fallback so a
 * brand-new tool is never shown raw to a layman.
 */
export function friendlyToolLabel(view: ToolCallView): FriendlyTool | null {
  const entry = TOOL_LABELS[view.name];
  if (!entry) return FALLBACK;
  if (entry.hidden) return null;
  return { icon: entry.icon, label: entry.refine?.(view) ?? entry.label };
}
